import { useEffect, useMemo, useRef, useState } from 'react';
import CustomToolTip from '../../../../../../CommonComponents/CustomToolTip'
import EmptyPage from '../../../../../custom_components/EmptyPage';
import { Command, CustomScheduleModalFormType, RecentlyEditedTelecommandType, ScheduleData, ScheduleTask } from '../../../../../types';
import { TaskUpcommingSvg } from '../../../modals/Svgs'
import { AddNewCommandIcon, ChevronDownSvg, NoCommandSelectedIcon } from '../../Svgs'
import NewAddButton from './NewAddButton';
import { get_task_status_icon, getContactCurrentStatus, MenuList } from '../../../../../utils';
import GroupButtonList from '../../../../../../CommonComponents/GroupButtonList';
import { base64ToHex } from '../../../../../../../utils/commonFunctions';
import { scrollAndBlinkEditedRow } from './scheduleRowUtils';

type TabType = 'details' | 'raw-data'

const Tabs = [
    { label: 'Details', value: 'details' },
    { label: 'Raw Data', value: 'raw-data' },
]

interface ScheduleTelecommandViewProps {
    showAddButton?: CustomScheduleModalFormType
    readonly: boolean;
    currentTime?: number;
    selectedSchedule?: ScheduleData | null;
    allTelecommands: Command[];
    selectedTelecommand: Command | null;
    recentlyEditedTelecommand?: RecentlyEditedTelecommandType | null;
    selectedTask: ScheduleTask | null;
    onMenuClick: (menu: string, view: 'readonly' | 'editable', type: string, data: { name: string, scheduleId: string, taskId?: string, commandId?: string }) => void;
    onCommandSelect: (view: 'readonly' | 'editable', command: Command) => void;
    onAddCommand?: (task: ScheduleTask, command: Command | null) => void;
}

const ScheduleTelecommandView = ({
    showAddButton,
    readonly,
    currentTime,
    selectedSchedule,
    allTelecommands,
    selectedTelecommand,
    recentlyEditedTelecommand,
    selectedTask,
    onMenuClick,
    onCommandSelect,
    onAddCommand
}: ScheduleTelecommandViewProps) => {
    const [expand, setExpand] = useState<boolean>(true);
    const [openMenu, setOpenMenu] = useState<string | boolean>(false)
    const [tab, setTab] = useState<TabType>('details')
    const recentlyEditedRowRef = useRef<HTMLDivElement | null>(null)
    const ongoingSchedule = useMemo(() => selectedSchedule?.status === 'RUNNING', [selectedSchedule])
    const executedSchedule = useMemo(() => selectedSchedule?.status === 'COMPLETED', [selectedSchedule])
    const hideGroupButtons = useMemo(() => ongoingSchedule || executedSchedule, [ongoingSchedule, executedSchedule])
    const scheduleId = useMemo(() => selectedSchedule?.scheduleId, [selectedSchedule])
    const filterTelecommands = useMemo(() => Array.from(new Map(allTelecommands.map(item => [item?.commandId, item])).values()), [allTelecommands])
    const recentlyEditedCommandId = recentlyEditedTelecommand?.commandId
    const recentlyEditedCommandKey = recentlyEditedTelecommand?.updatedAt

    // Auto-select first telecommand if none is selected
    useEffect(() => {
        if (readonly && !selectedTelecommand && !recentlyEditedCommandId && filterTelecommands.length > 0) {
            onCommandSelect('readonly', filterTelecommands[0]);
            setExpand(true);
        }
    }, [selectedTelecommand, recentlyEditedCommandId, filterTelecommands, onCommandSelect, readonly]);

    useEffect(() => {
        if (!recentlyEditedCommandId) return
        return scrollAndBlinkEditedRow(recentlyEditedRowRef.current)
    }, [recentlyEditedCommandId, recentlyEditedCommandKey]);
    
    const getCommandDetails = (start: number, end: number) => {
        if ((ongoingSchedule || executedSchedule || selectedSchedule) && (currentTime && readonly)) {
            const status = getContactCurrentStatus(start, end, currentTime)
            const icon = get_task_status_icon(start, end, currentTime)
            return {
                status,
                icon
            }
        }
        return {
            status: 'Upcoming',
            icon: <TaskUpcommingSvg />
        }
    }

    if (allTelecommands?.length === 0) {
        return (
            <div className='right__section__body'>
                <EmptyPage
                    classNames=''
                    icon={<NoCommandSelectedIcon />}
                    content='No Telecommands'
                    subContent=''
                    onClick={undefined}
                />
            </div>
        )
    }

    return (
        <div className='right__section__body'>
            <div className='schedule__task__container'>
                <div className='schedule__table__container'>
                    <div className='schedule__table__container__header'>
                        <div className='table__header__cell__container'>
                            <span className='table__cell header__cell w-4' />
                            <div className='table__cell header__cell w-25'>
                                <span className='cell__detail'>TELECOMMAND</span>
                            </div>
                            <div className='table__cell header__cell w-20'>
                                <span className='cell__detail'>SUBSYSTEM</span>
                            </div>
                            <div className='table__cell header__cell w-20'>
                                <span className='cell__detail'>TASK</span>
                            </div>
                            <div className='table__cell header__cell w-20'>
                                <span className='cell__detail'>START TIME</span>
                            </div>
                            <div className='table__cell header__cell w-10'>
                                <span className='cell__detail'>DURATION</span>
                            </div>
                        </div>
                    </div>

                    <div className='schedule__table__container__body'>
                        {filterTelecommands.map((item) => {
                            if (!item?.commandId) return null
                            const selected = item?.commandId === selectedTelecommand?.commandId
                            const recentlyEdited = item?.commandId === recentlyEditedCommandId
                            const view = readonly ? 'readonly' : 'editable'
                            const showButton = showAddButton === 'add-telecommand' && selected
                            const details = getCommandDetails(item?.epoch, item?.epoch + item?.duration)
                            return (
                                <div key={item?.commandId} className={`table__body__cell__container 
                                    ${selected ? 'active' : ''}
                                    ${recentlyEdited ? 'recently__edited__row' : ''}
                                    ${showButton ? 'show__button' : ''}
                                `}
                                    ref={recentlyEdited ? recentlyEditedRowRef : undefined}
                                    onClick={() => {
                                        setExpand(true)
                                        onCommandSelect(view, item)
                                    }}
                                >
                                    {!readonly &&
                                        <NewAddButton
                                            title='Add New Telecommand'
                                            onClick={() => { onAddCommand?.(selectedTask!, item) }}
                                        />
                                    }
                                    <div className='table__cell p-0 w-4'>
                                        <div className='cell__icon'>
                                            {(ongoingSchedule || executedSchedule) && <CustomToolTip title={details?.status || ''} placement='top-start'>
                                                {details?.icon}
                                            </CustomToolTip>}
                                        </div>
                                    </div>
                                    <div className='table__cell w-25'>
                                        <span className='cell__detail'>
                                            <CustomToolTip title={item?.name} placement='top-start'>
                                                {item?.name}
                                            </CustomToolTip>
                                        </span>
                                    </div>
                                    <div className='table__cell w-20'>
                                        <span className='cell__detail'>
                                            <CustomToolTip title={item?.subsystem} placement='top-start'>
                                                {item?.subsystem}
                                            </CustomToolTip>
                                        </span>
                                    </div>
                                    <div className='table__cell w-20'>
                                        <span className='cell__detail'>
                                            <CustomToolTip title={item?.taskName || ''} placement='top-start'>
                                                {item?.taskName}
                                            </CustomToolTip>
                                        </span>
                                    </div>
                                    <div className='table__cell w-20'>
                                        <span className='cell__detail'>
                                            <CustomToolTip title={item?.epochISO8601} placement='top-start'>
                                                {item?.epochISO8601}
                                            </CustomToolTip>
                                        </span>
                                    </div>
                                    <div className='table__cell w-10'>
                                        <span className='cell__detail'>
                                            <CustomToolTip title={`${item?.duration} sec`} placement='top-start'>
                                                {item?.duration ?? 0} sec
                                            </CustomToolTip>
                                        </span>
                                    </div>

                                    <div className='menu__container'>
                                        {!hideGroupButtons &&
                                            <GroupButtonList
                                                placement='left'
                                                id={item?.commandId as string}
                                                open={openMenu}
                                                toggle={(value) => {
                                                    setOpenMenu(value)
                                                }}
                                                buttons={MenuList}
                                                onClick={(btn) => {
                                                    onMenuClick(btn, view, 'telecommand', {
                                                        name: item?.name,
                                                        scheduleId: scheduleId as string,
                                                        taskId: item?.taskId as string,
                                                        commandId: item?.commandId as string,
                                                    })
                                                }}
                                            >
                                                <i className='fe fe-more-vertical opacity-75' />
                                            </GroupButtonList>
                                        }
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
            <div className={`schedule__telecommand__container small ${(expand && selectedTelecommand) ? 'active' : ''}`}>
                <div className='schedule__telecommand__container__header'>
                    <div className='schedule__telecommand__container__header__button__container'>
                        {Tabs.map((item) => {
                            return (
                                <button key={item.value} className={`telecommand__container__header__button ${tab === item.value ? 'active' : ''}`}
                                    onClick={() => {
                                        setTab(item.value as TabType)
                                        setExpand(true)
                                    }}
                                >
                                    {item.label}
                                </button>
                            )
                        })}
                    </div>
                    <CustomToolTip title={expand ? 'Close' : 'Open'} placement='top'>
                        <button className='schedule__telecommand__container__button close__button'
                            onClick={() => {
                                if(!selectedTelecommand) return
                                setExpand(!expand)
                            }}
                        >
                            <ChevronDownSvg />
                        </button>
                    </CustomToolTip>
                </div>
                <div className='schedule__table__container'>
                    {tab === 'details' ? (
                        <div className='schedule__table__container__body px-4 py-3'>
                            <div className='d-flex align-items-center gap-2'>
                                <span className='opacity-50 text-uppercase'>Name:</span>
                                <span>{selectedTelecommand?.name}</span>
                            </div>
                            <div className='d-flex align-items-center gap-2'>
                                <span className='opacity-50 text-uppercase'>Subsystem:</span>
                                <span>{selectedTelecommand?.subsystem}</span>
                            </div>
                            <div className='d-flex align-items-center gap-2'>
                                <span className='opacity-50 text-uppercase'>Task:</span>
                                <span>{selectedTask?.taskName}</span>
                            </div>
                            <div className='d-flex align-items-center gap-2'>
                                <span className='opacity-50 text-uppercase'>Start Time:</span>
                                <span>{selectedTelecommand?.epochISO8601}</span>
                            </div>
                            <div className='d-flex align-items-center gap-2'>
                                <span className='opacity-50 text-uppercase'>Duration:</span>
                                <span>{selectedTelecommand?.duration} sec</span>
                            </div>
                            <div className='d-flex align-items-start gap-2'>
                                <span className='opacity-50 text-uppercase'>Command Data:</span>
                                <code className='w-80 text-white'>{JSON.stringify(selectedTelecommand?.telecommandDetails?.telecommand, null, 4)}</code>
                            </div>
                        </div>
                    ) : (
                        <div className='schedule__table__container ps-4 py-3'>
                            <div className='schedule__table__container__body '>
                                <div className='row__data__container'>
                                    <div className='row__data__details__container'>
                                        <span className='row__data__details__text d-flex'>
                                            TC/TM COMMAND | {selectedTelecommand?.subsystem}
                                        </span>
                                        <code className='row__data__details__text w-full'>
                                            {base64ToHex(selectedTelecommand?.sequenceDetails?.sequenceBase64)}
                                        </code>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ScheduleTelecommandView
